// CWR 3.1 File Generator in TypeScript

import * as CWR from './cwr_lookup'; // Assuming your lookup tables are in CWR_Lookup.ts

// --- Interfaces for input JSON (based on GraphQL editionOne query) ---

export interface EditionData {
  id: string;
  name: string; // Edition/Album name
  editionNumber?: string;
  publicationDate?: string; // YYYY-MM-DD or similar
  works: WorkData[];
}

export interface WorkData {
  id: string; // Submitter's work ID
  title: string;
  alternativeTitles?: string[];
  cmsRegistrationDate?: string;
  cmsId?: string;
  musicalGenre?: {
    id: string;
    name: string;
  };
  territories?: { name: string }[]; // Assumed to be territories of control for the work
  iswc?: string;
  isrc?: string;
  originalLyrics?: boolean;
  notes?: string;
  audioFile?: any; // Structure not defined, used to infer 'Recorded Indicator'
  authors: AuthorContribution[];
}

export interface AuthorContribution {
  id: string; // ID of the author's contribution to this work
  author: AuthorData;
  lyricsPercentage?: number; // e.g., 50 for 50%
  musicPercentage?: number;  // e.g., 50 for 50%
}

export interface AuthorData {
  id: string; // Author's unique ID
  firstName?: string;
  lastName: string;
  taxId?: string;
  email?: string;
  nationality?: { id: string };
  dateOfBirth?: string;
  address?: any; // Structure not fully defined
  aka?: string;
  ipi?: string; // IPI Name Number (e.g., 00012345678)
  professionalInformation?: {
    defaultContractDuration?: { durationInYears?: number };
    managementSocieties?: { id: string }[]; // Society codes
    publishers?: PublisherContribution[];
  };
}

export interface PublisherContribution {
  percentage: number; // Publisher's share of author's income
  publisher: PublisherData;
}

export interface PublisherData {
  name: string;
  // CRITICAL GAP: IPI Number for publisher is missing in GraphQL
  // id?: string; // Potentially publisher's unique ID if available
  // ipi?: string; // Publisher IPI Name Number
}

// --- Configuration (to be filled by the user/system) ---
export interface CWRSubmitterConfig {
  senderId: string; // Your CWR Sender ID (e.g., "MYPUB")
  senderName: string; // Your Publisher Name
  senderIpiNameNumber?: string; // Your IPI Name # (11 digits)
  recipientId?: string; // Recipient Society CWR ID (e.g., "058" for SACEM)
  recipientName?: string; // Recipient Society Name
}

const DEFAULT_CONFIG: CWRSubmitterConfig = {
  senderId: 'MYPUB', // FIXME: Configure your Sender ID
  senderName: 'MY MUSIC PUBLISHING INC', // FIXME: Configure your Sender Name
  senderIpiNameNumber: '00012345600', // FIXME: Configure your IPI Name # (11 digits) or leave undefined
  // recipientId: '000', // Use '000' for multiple recipients or specify one
  // recipientName: 'VARIOUS RECIPIENTS',
};

// --- Helper Functions ---

function padString(value: string | undefined | null, length: number, char = ' '): string {
  const str = String(value || '');
  return str.padEnd(length, char).substring(0, length);
}

function padNumber(value: number | undefined | null, length: number, char = '0'): string {
  const numStr = String(value === undefined || value === null ? '' : value);
  return numStr.padStart(length, char).substring(0, length);
}

function formatDate(dateString: string | undefined, type: 'YYYYMMDD' | 'HHMMSS' | 'YYMMDD'): string {
  if (!dateString) {
    return type === 'YYYYMMDD' ? '00000000' : '000000';
  }
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return type === 'YYYYMMDD' ? '00000000' : '000000';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    if (type === 'YYYYMMDD') return `${year}${month}${day}`;
    if (type === 'HHMMSS') return `${hours}${minutes}${seconds}`;
    if (type === 'YYMMDD') return `${String(year).slice(-2)}${month}${day}`;
  } catch (e) {
    return type === 'YYYYMMDD' ? '00000000' : '000000';
  }
  return type === 'YYYYMMDD' ? '00000000' : '000000';
}

function formatPercentage(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(value)) {
    return '00000'; // N5.2, e.g., 0.00%
  }
  // Assuming value is like 50.00 for 50%
  const val = Math.min(100, Math.max(0, value)); // Clamp between 0 and 100
  // CWR N5.2 format: 50.00% -> 05000, 5.25% -> 00525
  const formatted = (val * 100).toFixed(0); // Multiply by 100 and remove decimals for CWR (e.g. 50% -> 5000)
  return padNumber(parseInt(formatted), 5);
}

function getCurrentDateTime() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  return {
    date: `${year}${month}${day}`,
    time: `${hours}${minutes}${seconds}`,
    yearShort: String(year).slice(-2),
  };
}

// --- CWR Record Generation Functions ---

let recordSequenceNumber = 0;

function getRecordSequenceNumber(): string {
    recordSequenceNumber++;
    return padNumber(recordSequenceNumber, 8);
}

function generateHDR(config: CWRSubmitterConfig, creationDate: string, creationTime: string): string {
  let record = 'HDR';
  const senderType = config.senderIpiNameNumber && config.senderIpiNameNumber.length === 11 ? '  ' : 'PB';
  record += padString(senderType, 2);
  record += padString(config.senderId, 9);
  record += padString(config.senderName, 45);
  record += padString(undefined, 11);
  record += padString(config.senderIpiNameNumber, 11);
  record += padString(undefined, 7);
  record += padString(creationDate, 8);
  record += padString(creationTime, 6);
  record += padString(creationDate, 8);
  record += padString(undefined, 5);
  record += padString('0310', 4);
  record += padString(config.recipientId, 9);
  record += padString(config.recipientName, 45);
  record += padString(CWR.CharacterSetCodes.ISO_8859_1, 11); // Default ISO-8859-1
  record += padString(undefined, 76);
  return record + '\r\n';
}

function generateGRH(groupId: number, transactionType: string = 'WRK'): string {
  let record = 'GRH';
  record += padString(transactionType, 3);
  record += padNumber(groupId, 5);
  record += padString('0310', 4);
  record += padString(undefined, 10);
  record += padString(undefined, 235);
  return record + '\r\n';
}

function generateWRK(work: WorkData, submitterWorkId: string, editionPublicationDate?: string): string {
  let record = 'WRK';
  record += getRecordSequenceNumber();
  record += padString(work.title, 60);
  record += padString('EN', 2); // Default to English // FIXME: Determine language
  record += padString(submitterWorkId, 14);
  record += padString(work.iswc, 11);
  record += formatDate(work.cmsRegistrationDate || editionPublicationDate, 'YYYYMMDD'); // Copyright Date
  record += padString(undefined, 12); // Copyright Number

  let musicalDistCategory: CWR.MusicalWorkDistributionCategoryCode = 'UNC'; // Default
  if (work.musicalGenre?.name) {
      const genre = work.musicalGenre.name.toUpperCase();
      if (genre.includes('JAZZ')) musicalDistCategory = 'JAZ';
      else if (genre.includes('POP') || genre.includes('ROCK') || genre.includes('URBAN')) musicalDistCategory = 'POP';
      else if (genre.includes('CLASSICAL') || genre.includes('SERIOUS') || genre.includes('SYMPHONIC')) musicalDistCategory = 'SER';
  }
  record += padString(CWR.MusicalWorkDistributionCategoryCodes[musicalDistCategory], 3);

  record += padString(undefined, 6); // Duration HHMMSS - // FIXME: Not in GraphQL
  record += padString(work.isrc || work.audioFile ? 'Y' : 'N', 1); // Recorded Indicator

  let textMusicRel: CWR.TextMusicRelationshipCode = 'MTX'; // Default
  let hasMusic = false;
  let hasLyrics = false;
  work.authors.forEach(ac => {
      if ((ac.musicPercentage ?? 0) > 0) hasMusic = true;
      if ((ac.lyricsPercentage ?? 0) > 0) hasLyrics = true;
  });
  if (hasMusic && !hasLyrics) textMusicRel = 'MUS';
  else if (!hasMusic && hasLyrics) textMusicRel = 'TXT';
  record += padString(CWR.TextMusicRelationshipCodes[textMusicRel], 3);

  record += padString(undefined, 3); // Composite Type
  record += padString('ORI', 3); // Version Type - Default to Original
  record += padString(undefined, 3); // Excerpt Type
  record += padString(undefined, 3); // Music Arrangement
  record += padString(undefined, 3); // Lyric Adaptation
  record += padString(undefined, 1); // Grand Rights Indicator (Pos 147) - // FIXME
  record += padNumber(undefined, 3); // Composite Component Count (Pos 148-150) - // FIXME
  record += formatDate(undefined, 'YYYYMMDD'); // Date of Publication of Printed Edition (Pos 151-158) - // FIXME
  record += padString(undefined, 1); // Exceptional Clause (Pos 159) - // FIXME
  record += padString(undefined, 15); // Opus Number (Pos 160-174) - // FIXME
  record += padString(undefined, 15); // Catalogue Number (Pos 175-189) - // FIXME
  record += padString(undefined, 1); // Priority Flag (Pos 190) - // FIXME
  record += padString(undefined, 70); // Filler
  return record + '\r\n';
}

function generateALT(title: string, titleType: CWR.TitleTypeCode = 'AT', lang?: string): string {
    let record = 'ALT';
    record += getRecordSequenceNumber();
    record += padString(title, 60);
    record += padString(titleType, 2); // Use the key directly
    record += padString(lang || 'EN', 2);
    record += padString(undefined, 185);
    return record + '\r\n';
}

function generateSWR(authorCont: AuthorContribution): string { // Removed 'work' parameter
    const author = authorCont.author;
    let record = 'SWR';
    record += getRecordSequenceNumber();
    record += padString(author.id, 9);
    record += padString(author.lastName, 45);
    record += padString(author.firstName, 30);
    record += 'N'; // Writer Unknown Indicator

    let writerRole: CWR.WriterRoleCode = 'CA'; // Default Composer/Author
    const musicPerc = authorCont.musicPercentage ?? 0;
    const lyricsPerc = authorCont.lyricsPercentage ?? 0;
    if (musicPerc > 0 && lyricsPerc === 0) writerRole = 'C';
    else if (musicPerc === 0 && lyricsPerc > 0) writerRole = 'A';
    // FIXME: Add logic for other roles if applicable (AD, AR, TR etc.)
    record += padString(writerRole, 2); // Use the key directly

    record += padString(author.taxId, 9);
    record += padString(author.ipi, 11);
    record += padString(author.ipi ? author.ipi.substring(0,10) + '1' : undefined, 11); // FIXME: Derive correctly
    record += padString(undefined, 9);
    record += padString(undefined, 1); // USA License Ind

    const totalManuscriptShare = Math.min(100, (musicPerc + lyricsPerc)); // Simplistic sum
    record += formatPercentage(totalManuscriptShare);
    record += formatPercentage(totalManuscriptShare);
    record += formatPercentage(totalManuscriptShare);

    record += padString(undefined, 106);
    return record + '\r\n';
}

function generateSWT(
    authorId: string,
    territories: { name: string }[] | undefined,
    managementSocieties: { id: string }[] | undefined
): string[] {
    const swtRecords: string[] = [];
    const prCollectionShare = formatPercentage(0);
    const mrCollectionShare = formatPercentage(0);
    const srCollectionShare = formatPercentage(0);

    const prSociety = managementSocieties?.[0]?.id; // Get first society ID if available
    const mrSociety = managementSocieties?.[0]?.id;
    const srSociety = managementSocieties?.[0]?.id;

    (territories || [{ name: 'Worldwide' }]).forEach(terr => {
        let record = 'SWT';
        record += getRecordSequenceNumber();
        record += padString(authorId, 9);
        record += prCollectionShare;
        record += mrCollectionShare;
        record += srCollectionShare;
        record += 'I';

        let tisCode: string = CWR.TisCodes["2136"]; // Default to World (value)
        if (terr.name.toUpperCase().includes("UNITED STATES") || terr.name.toUpperCase().includes("USA")) tisCode = CWR.TisCodes["0840"];
        else if (terr.name.toUpperCase().includes("ARGENTINA")) tisCode = CWR.TisCodes["0032"];
        record += padString(tisCode, 4);

        record += padString(prSociety, 4);
        record += padString(mrSociety, 4);
        record += padString(srSociety, 4);
        record += padString(undefined, 208);
        swtRecords.push(record + '\r\n');
    });
    return swtRecords;
}

function generateSPU(publisherData: PublisherData, pubShareOfAuthorIncome: number, authorShares: { lyrics: number, music: number}, publisherIpId: string): string { // Removed 'author' parameter
    let record = 'SPU';
    record += getRecordSequenceNumber();
    record += padString(publisherIpId, 9);
    record += padString(publisherData.name, 45);
    record += 'N';
    record += padString(CWR.PublisherRoleCodes.E, 2); // Default to Original Publisher
    record += padString(undefined, 9);

    record += padString('00000000000', 11); // Placeholder for missing Publisher IPI
    record += padString('00000000001', 11); // Placeholder for missing Publisher IPI Base

    record += padString(undefined, 1);

    const authorTotalManuscript = (authorShares.lyrics + authorShares.music);
    const publisherWorkShare = (authorTotalManuscript / 100) * (pubShareOfAuthorIncome / 100) * 100;

    record += formatPercentage(publisherWorkShare);
    record += formatPercentage(publisherWorkShare);
    record += formatPercentage(publisherWorkShare);

    record += padString(undefined, 145);
    return record + '\r\n';
}

function generateSPT(
    publisherIpId: string,
    publisherWorkShare: number,
    territories: { name: string }[] | undefined,
    managementSocieties: { id: string }[] | undefined
): string[] {
    const sptRecords: string[] = [];
    const collectionShare = formatPercentage(publisherWorkShare);

    const prSociety = managementSocieties?.[0]?.id;
    const mrSociety = managementSocieties?.[0]?.id;
    const srSociety = managementSocieties?.[0]?.id;

    (territories || [{ name: 'Worldwide' }]).forEach(terr => {
        let record = 'SPT';
        record += getRecordSequenceNumber();
        record += padString(publisherIpId, 9);
        record += collectionShare;
        record += collectionShare;
        record += collectionShare;
        record += 'I';

        let tisCode: string = CWR.TisCodes["2136"]; // Default to World (value)
        if (terr.name.toUpperCase().includes("UNITED STATES") || terr.name.toUpperCase().includes("USA")) tisCode = CWR.TisCodes["0840"];
        else if (terr.name.toUpperCase().includes("ARGENTINA")) tisCode = CWR.TisCodes["0032"];
        record += padString(tisCode, 4);

        record += padString(prSociety, 4);
        record += padString(mrSociety, 4);
        record += padString(srSociety, 4);

        record += padString(undefined, 10);
        record += padString(undefined, 14);
        record += padString(CWR.AgreementTypeCodes.OG, 2); // Default Original General (value)
                                                        // Actually, this should be the key 'OG'
        record += padString('OG', 2); // Corrected to use key 'OG'

        record += padString(undefined, 180);
        sptRecords.push(record + '\r\n');
    });
    return sptRecords;
}

function generatePWR(writerIpId: string, publisherIpId: string, agreementType: CWR.AgreementTypeCode = 'OG'): string {
    let record = 'PWR';
    record += getRecordSequenceNumber();
    record += padString(publisherIpId, 9);
    record += padString(writerIpId, 9);
    record += padString(undefined, 4);
    record += padString(undefined, 4);
    record += padString(undefined, 4);
    record += padString(undefined, 10);
    record += padString(agreementType, 2); // Use the key directly
    record += padString(undefined, 14);
    record += padString(undefined, 193);
    return record + '\r\n';
}

function generateREC(work: WorkData, editionName?: string): string {
    if (!work.isrc) return '';
    let record = 'REC';
    record += getRecordSequenceNumber();
    record += formatDate(undefined, 'YYYYMMDD');
    record += padString(undefined, 6);
    record += padString(editionName, 60); // Album Title from editionData.name
    record += padString(undefined, 60);
    record += padString(undefined, 14);
    record += padString(undefined, 13);
    record += padString(work.isrc, 12);
    record += padString(undefined, 3);
    record += padString(undefined, 3);
    record += padString(undefined, 3);
    record += padString(work.title, 60);

    const fullRecLength = 400; // CWR 3.1 REC length
    // Simplified: fill up to ISRC Validity Indicator at pos 380 for CWR 3.1
    // This is a placeholder and needs full CWR 3.1 REC spec.
    let currentLength = record.length;
    let fillerBeforeValidity = '';
    if (379 > currentLength) { // Position 380 is 1-based index, so 379 for 0-based string
        fillerBeforeValidity = padString(undefined, 379 - currentLength);
    }
    record += fillerBeforeValidity;
    record += 'Y'; // ISRC Validity Indicator at pos 380 (char index 379)

    currentLength = record.length;
    if (currentLength < fullRecLength) {
        record += padString(undefined, fullRecLength - currentLength);
    }

    return record.substring(0, fullRecLength) + '\r\n';
}


function generateGRT(groupId: number, transactionCount: number, recordCountInGroup: number): string {
  let record = 'GRT';
  record += padNumber(groupId, 5);
  record += padNumber(transactionCount, 8);
  record += padNumber(recordCountInGroup, 8);
  record += padString(undefined, 236);
  return record + '\r\n';
}

function generateTRL(groupCount: number, totalTransactionCount: number, totalRecordCount: number): string {
  let record = 'TRL';
  record += padNumber(groupCount, 8);
  record += padNumber(totalTransactionCount, 8);
  record += padNumber(totalRecordCount, 8);
  record += padString(undefined, 233);
  return record + '\r\n';
}


// --- Main CWR Generation Function ---

export function generateCWRFile(editionData: EditionData, configOverrides?: Partial<CWRSubmitterConfig>): string {
  const config = { ...DEFAULT_CONFIG, ...configOverrides };
  const { date: creationDate, time: creationTime } = getCurrentDateTime();

  let cwrFileContent = '';
  let totalRecordCount = 0;
  let totalTransactionCount = 0;
  let groupCount = 0;

  const hdrRecord = generateHDR(config, creationDate, creationTime);
  cwrFileContent += hdrRecord;
  totalRecordCount++;
  recordSequenceNumber = 0;

  groupCount++;
  const groupId = groupCount;
  const grhRecord = generateGRH(groupId, 'WRK');
  cwrFileContent += grhRecord;
  totalRecordCount++;

  let recordsInCurrentGroup = 0;
  let transactionsInCurrentGroup = 0;

  editionData.works.forEach((work, workIndex) => {
    transactionsInCurrentGroup++;
    totalTransactionCount++;

    const submitterWorkId = work.id || `WORK${padNumber(workIndex + 1, 5)}`;

    const wrkRecord = generateWRK(work, submitterWorkId, editionData.publicationDate);
    cwrFileContent += wrkRecord; recordsInCurrentGroup++; totalRecordCount++;

    (work.alternativeTitles || []).forEach(altTitle => {
        cwrFileContent += generateALT(altTitle); recordsInCurrentGroup++; totalRecordCount++;
    });

    const publisherIpMap = new Map<string, string>();

    work.authors.forEach((authorCont, authorIndex) => {
      const author = authorCont.author;
      const authorIpId = author.id || `AUT${padNumber(authorIndex + 1, 3)}`;

      cwrFileContent += generateSWR(authorCont); recordsInCurrentGroup++; totalRecordCount++;

      const swtRecords = generateSWT(authorIpId, work.territories, author.professionalInformation?.managementSocieties);
      swtRecords.forEach(swt => { cwrFileContent += swt; recordsInCurrentGroup++; totalRecordCount++; });

      (author.professionalInformation?.publishers || []).forEach((pubCont, pubIndex) => {
        const publisher = pubCont.publisher;
        const publisherKeyForMap = `${publisher.name}-${pubIndex}`;
        let publisherIpId = publisherIpMap.get(publisherKeyForMap);
        if (!publisherIpId) {
            publisherIpId = `PUB${padNumber(workIndex+1,2)}${padNumber(authorIndex+1,2)}${padNumber(pubIndex+1,2)}`;
            publisherIpMap.set(publisherKeyForMap, publisherIpId);

            cwrFileContent += generateSPU(
                publisher,
                pubCont.percentage,
                { lyrics: authorCont.lyricsPercentage ?? 0, music: authorCont.musicPercentage ?? 0 },
                publisherIpId
            );
            recordsInCurrentGroup++; totalRecordCount++;

            const authorTotalManuscript = (authorCont.lyricsPercentage ?? 0) + (authorCont.musicPercentage ?? 0);
            const publisherWorkShareCalc = (authorTotalManuscript / 100) * (pubCont.percentage / 100) * 100;

            const sptRecords = generateSPT(
                publisherIpId,
                publisherWorkShareCalc,
                work.territories,
                author.professionalInformation?.managementSocieties
            );
            sptRecords.forEach(spt => { cwrFileContent += spt; recordsInCurrentGroup++; totalRecordCount++; });
        }

        cwrFileContent += generatePWR(authorIpId, publisherIpId); recordsInCurrentGroup++; totalRecordCount++;
      });
    });

    const recRecord = generateREC(work, editionData.name);
    if (recRecord) {
        cwrFileContent += recRecord; recordsInCurrentGroup++; totalRecordCount++;
    }

    if (work.cmsId) {
        let xrfRecord = 'XRF';
        xrfRecord += getRecordSequenceNumber();
        xrfRecord += padString('MYCMS', 8); // FIXME: Needs config
        xrfRecord += padString(work.cmsId, 28);
        xrfRecord += padString(CWR.IdentifierTypeCodes.W, 1);
        xrfRecord += 'Y';
        xrfRecord += padString(undefined, 222);
        cwrFileContent += xrfRecord + '\r\n';
        recordsInCurrentGroup++; totalRecordCount++;
    }

    if (work.notes) {
        let ariRecord = 'ARI';
        ariRecord += getRecordSequenceNumber();
        ariRecord += padString(undefined, 4);
        ariRecord += padString(undefined, 14);
        ariRecord += padString('ALL', 3);
        ariRecord += padString('GEN', 3);
        ariRecord += padString(work.notes, 160);
        ariRecord += padString(undefined, 76);
        cwrFileContent += ariRecord + '\r\n';
        recordsInCurrentGroup++; totalRecordCount++;
    }
  });

  const grtRecord = generateGRT(groupId, transactionsInCurrentGroup, recordsInCurrentGroup + 2);
  cwrFileContent += grtRecord;
  totalRecordCount++;

  const trlRecord = generateTRL(groupCount, totalTransactionCount, totalRecordCount + 1);
  cwrFileContent += trlRecord;

  return cwrFileContent;
}
